import React, { useCallback, useEffect, useState } from 'react';

import { AlertTriangle, ClipboardCheck, FileText, Video } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import {
  EvaluationDetails,
  VideoAnalytics,
  VideoUpload as VideoUploadType,
  evaluationService,
} from '../../services/evaluationService';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { cn, formatDate } from '../../utils';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader } from '../common/Card';
import { Modal } from '../modals/Modal';

import { AiScoringPipelineCard } from './AiScoringPipelineCard';
import VideoUpload from './VideoUpload';


interface EvaluationDetailEnhancedProps {
  className?: string;
}

const getVideoStatusColor = (status: string) => {
  switch (status) {
    case 'processed':
      return 'bg-green-100 text-green-800';
    case 'processing':
    case 'uploading':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getStatusColor = (status: EvaluationDetails['status']) => {
  switch (status) {
    case 'completed':
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-purple-100 text-purple-800';
    case 'in_progress':
    case 'scored':
      return 'bg-blue-100 text-blue-800';
    case 'archived':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const EvaluationDetailEnhanced: React.FC<
  EvaluationDetailEnhancedProps
> = ({ className }) => {
  const { id: evaluationId } = useParams<{ id: string }>();
  const { user } = useAppSelector(state => state.auth);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState<EvaluationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [videos, setVideos] = useState<VideoUploadType[]>([]);
  const [videoAnalytics, setVideoAnalytics] = useState<
    Record<number, VideoAnalytics>
  >({});

  const isTeacher = user?.role === UserRole.TEACHER;
  // Teachers upload their own lesson videos; evaluators/admins can also
  // attach observation footage while the evaluation is open.
  const canUploadVideo =
    hasPermission(APP_PERMISSIONS.UPLOAD_VIDEO) &&
    evaluation !== null &&
    (!isTeacher || evaluation.teacher_id === user?.id) &&
    !['completed', 'approved', 'archived'].includes(evaluation.status);
  // Teachers are the evaluated party; everyone else with access can score.
  const canScore = !isTeacher && evaluation !== null;

  const loadVideos = useCallback(async () => {
    if (!evaluation) return;
    try {
      const uploads = await evaluationService.listVideoUploads(
        evaluation.teacher_id
      );
      setVideos(uploads);
      const processed = uploads.filter(v => v.status === 'processed');
      const analyticsEntries = await Promise.all(
        processed.map(async v => {
          try {
            return [v.id, await evaluationService.getVideoAnalytics(v.id)] as const;
          } catch {
            return null;
          }
        })
      );
      setVideoAnalytics(
        Object.fromEntries(
          analyticsEntries.filter(
            (entry): entry is readonly [number, VideoAnalytics] =>
              entry !== null
          )
        )
      );
    } catch {
      // Video list is supplementary; leave the card empty on failure.
    }
  }, [evaluation]);

  useEffect(() => {
    void loadVideos();
  }, [loadVideos]);

  // Poll while any video is still being processed.
  useEffect(() => {
    const active = videos.some(v =>
      ['pending', 'uploading', 'uploaded', 'processing'].includes(v.status)
    );
    if (!active) return undefined;
    const timer = setTimeout(() => void loadVideos(), 3000);
    return () => clearTimeout(timer);
  }, [videos, loadVideos]);

  const loadEvaluation = useCallback(async () => {
    if (!evaluationId) {
      setError('Evaluation ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setEvaluation(await evaluationService.getEvaluation(evaluationId));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load evaluation'
      );
    } finally {
      setLoading(false);
    }
  }, [evaluationId]);

  useEffect(() => {
    void loadEvaluation();
  }, [loadEvaluation]);

  const handleVideoUploadComplete = (_upload: VideoUploadType) => {
    setShowVideoUpload(false);
    void loadVideos();
  };

  const handleRetryProcessing = async (uploadId: number) => {
    try {
      await evaluationService.triggerVideoProcessing(uploadId);
      void loadVideos();
    } catch {
      // Status polling will surface any persistent failure.
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-gray-600">Loading evaluation...</p>
        </div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="py-8 text-center">
        <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-500" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900">
          Error Loading Evaluation
        </h3>
        <p className="mb-4 text-gray-600">{error || 'Evaluation not found'}</p>
        <Button onClick={() => navigate('/evaluations')}>
          Back to Evaluations
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('mx-auto max-w-7xl space-y-6', className)}>
      <div className="flex items-start justify-between bg-white px-6 py-4 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Evaluation Detail
          </h1>
          <p className="mt-1 text-gray-600">
            {evaluation.teacher?.full_name ||
              `Teacher ${evaluation.teacher_id}`}
          </p>
        </div>
        <Badge className={getStatusColor(evaluation.status)}>
          {evaluation.status.replace('_', ' ').toUpperCase()}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Evaluation Information</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Period</p>
              <p className="text-gray-900">{evaluation.evaluation_period}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Template</p>
              <p className="text-gray-900">
                {evaluation.template?.name || evaluation.template_id}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Created</p>
              <p className="text-gray-900">
                {formatDate(evaluation.created_at)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Score</p>
              <p className="text-gray-900">
                {evaluation.percentage_score === null
                  ? 'Not scored'
                  : `${evaluation.percentage_score.toFixed(1)}%`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Lesson Video</h2>
            {canUploadVideo && (
              <Button onClick={() => setShowVideoUpload(true)}>
                <Video className="mr-2 h-4 w-4" />
                Upload Video
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {videos.length === 0 ? (
            <p className="text-sm text-gray-600">
              No lesson videos uploaded for this teacher yet.
              {canUploadVideo &&
                ' Upload a classroom video to run automated analysis (motion, faces, audio).'}
            </p>
          ) : (
            <div className="space-y-4">
              {videos.map(video => {
                const analytics = videoAnalytics[video.id];
                return (
                  <div
                    key={video.id}
                    className="rounded-lg border border-gray-200 p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <Video className="h-5 w-5 text-blue-500" />
                        <span className="font-medium text-gray-900">
                          {video.filename}
                        </span>
                        <Badge className={getVideoStatusColor(video.status)}>
                          {video.status.toUpperCase()}
                        </Badge>
                      </div>
                      {video.status === 'failed' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void handleRetryProcessing(video.id)}
                        >
                          Retry Processing
                        </Button>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                      <span>Duration: {formatDuration(video.duration_seconds)}</span>
                      {video.resolution && <span>{video.resolution}</span>}
                      {video.quality_assessment && (
                        <span>Quality: {video.quality_assessment}</span>
                      )}
                      <span>
                        FERPA: {video.ferpa_compliant ? 'Compliant' : 'Review needed'}
                      </span>
                      <span>Uploaded: {formatDate(video.created_at)}</span>
                    </div>

                    {video.status === 'failed' && video.error_message && (
                      <p className="mt-2 text-sm text-red-600">
                        {video.error_message}
                      </p>
                    )}

                    {['pending', 'uploading', 'uploaded', 'processing'].includes(
                      video.status
                    ) && (
                      <p className="mt-2 text-sm text-blue-600">
                        Analyzing video (metadata, faces, motion, audio)…
                      </p>
                    )}

                    {analytics && (
                      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 sm:grid-cols-4">
                        <div>
                          <p className="text-xs font-medium text-gray-500">
                            Movement Score
                          </p>
                          <p className="text-gray-900">
                            {analytics.teacher_movement_score !== null
                              ? analytics.teacher_movement_score.toFixed(3)
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">
                            Engagement Score
                          </p>
                          <p className="text-gray-900">
                            {analytics.student_engagement_score !== null
                              ? analytics.student_engagement_score.toFixed(3)
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">
                            Audio Duration
                          </p>
                          <p className="text-gray-900">
                            {formatDuration(analytics.total_speech_time)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">
                            Faces Detected
                          </p>
                          <p className="text-gray-900">{video.faces_detected}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-gray-500">
                Scores are deterministic v1 metrics (frame-difference motion,
                OpenCV face detection, audio duration). Speech attribution and
                sentiment analysis are not yet implemented.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AiScoringPipelineCard
        evaluationId={evaluation.id}
        canManage={canScore}
        videos={videos}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Rubric Scoring</h2>
            <div className="flex items-center gap-2">
              {['scored', 'completed'].includes(evaluation.status) && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate(`/evaluations/${evaluation.id}/report`)
                  }
                >
                  <FileText className="mr-2 h-4 w-4" />
                  View Report
                </Button>
              )}
              {canScore && (
                <Button
                  onClick={() =>
                    navigate(`/evaluations/${evaluation.id}/score`)
                  }
                >
                  <ClipboardCheck className="mr-2 h-4 w-4" />
                  {evaluation.status === 'scored'
                    ? 'Review / Re-score'
                    : 'Score Evaluation'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            {evaluation.status === 'scored'
              ? 'This evaluation has been scored. Open the scoring view to review or adjust component scores.'
              : 'Score this evaluation against its rubric: expand each domain, select a performance level per component, and save.'}
          </p>
        </CardContent>
      </Card>

      <Modal
        isOpen={showVideoUpload}
        onClose={() => setShowVideoUpload(false)}
        title="Upload Lesson Video"
        size="2xl"
      >
        <VideoUpload
          teacherId={evaluation.teacher_id}
          evaluationPurpose={`Evaluation ${evaluation.id}`}
          onUploadComplete={handleVideoUploadComplete}
          onCancel={() => setShowVideoUpload(false)}
        />
      </Modal>
    </div>
  );
};

export default EvaluationDetailEnhanced;
